import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useToggleReactionMutation } from 'generated/graphql';
import { applyReactionToggle } from '../lib/applyReactionToggle';
import { CommunityId } from '../model/community.types';
import { ReactionParent, ReactionType } from '../model/post.types';
import { ReplyId } from '../model/reply.types';

type CachedReaction = {
  __typename: 'Reaction';
  type: ReactionType;
  count: number;
  isToggled: boolean;
};

export const useToggleReplyReaction = (communityId: CommunityId) => {
  const [toggleReactionMutation] = useToggleReactionMutation();

  return useCallback(
    (replyId: ReplyId, type: ReactionType) =>
      toggleReactionMutation({
        variables: {
          communityId,
          entityId: replyId,
          parent: ReactionParent.Reply,
          type,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          toggleReaction: { __typename: 'Post', id: replyId },
        },
        update: (cache) => {
          const cacheId = cache.identify({ __typename: 'Reply', id: replyId });
          if (cacheId === undefined) return;

          cache.modify<{ reactions: CachedReaction[] }>({
            id: cacheId,
            fields: {
              // Reaction has no id, so the cache stores reactions inline and never as
              // References.
              reactions: (existing = []) =>
                applyReactionToggle(
                  [...(existing as readonly CachedReaction[])],
                  type,
                ).map((reaction) => ({
                  __typename: 'Reaction' as const,
                  ...reaction,
                })),
            },
          });
        },
      }).catch(() => {
        toast.error('Something went wrong');
      }),
    [toggleReactionMutation, communityId],
  );
};
